import { makeStyles, Theme } from '@material-ui/core';
import { SvgIconComponent } from '@material-ui/icons';
import clsx from 'clsx';
import { ReactNode } from 'react';

import WithChildren from '../types/WithChildren';

import InfoTooltip from './InfoTooltip';

const useStyles = makeStyles<Theme, { small: boolean }>((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: ({ small }) => (small ? '1.5rem' : '2.5rem'),
    marginRight: theme.spacing(1),
  },
  // Lets the text shrink below its longest word, so a long value wraps (hyphenated where the browser can) instead of
  // overflowing into the next tile
  content: {
    minWidth: 0,
    overflowWrap: 'break-word',
    hyphens: 'auto',
  },
  label: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  value: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.25rem',
    },
  },
  // Declared after `value` so it also wins over the breakpoint rule
  valueSmall: {
    fontSize: '1rem',
  },
  padding: {
    padding: '6px',
  },
}));

type IconValueProps = {
  hint?: string | ReactNode;
  icon: SvgIconComponent;
  label: string;
  small?: boolean;
  color?: string;
  padding?: boolean;
};

const IconValue = ({
  hint,
  icon: Icon,
  label,
  children,
  small = false,
  color,
  padding = false,
}: WithChildren<IconValueProps>) => {
  const classes = useStyles({ small });

  return (
    <div className={clsx(classes.root, padding && classes.padding)}>
      <Icon className={classes.icon} style={color ? { color } : {}} />
      <div className={classes.content}>
        <div className={classes.label}>
          {label}
          {hint && <InfoTooltip hint={hint} xsIcon />}
        </div>
        <div className={clsx(classes.value, small && classes.valueSmall)}>{children}</div>
      </div>
    </div>
  );
};

export default IconValue;
